import dayjs from "dayjs";
import { store } from "../../store/store";
import tourFlagForPath from "../tourFlags";
import { enabledModules } from "../modules";
import {
  TeamOutlined,
  ClusterOutlined,
  DashboardOutlined,
  LayoutOutlined,
  OrderedListOutlined,
  UnorderedListOutlined,
  IdcardOutlined,
  UsergroupAddOutlined,
  BuildOutlined,
  ClockCircleOutlined,
  BarChartOutlined,
  SettingOutlined,
  ApartmentOutlined,
  TagOutlined,
  MessageOutlined,
  CustomerServiceOutlined,
  CalendarOutlined,
  CrownOutlined,
  MailOutlined,
  ScheduleOutlined,
  CrownFilled,
} from "@ant-design/icons";
import moment from "moment-jalaali";
import ApexCharts from "apexcharts";
import * as XLSX from "xlsx";

export function removeEmpty(obj) {
  return Object.fromEntries(Object.entries(obj).filter(([_, v]) => v != null));
}

export function toLocalDateString(date) {
  const value = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(value.getTime())) return undefined;

  const pad = (part) => String(part).padStart(2, "0");
  return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`;
}

export function apiParams(searchParams) {
  const out = {};
  for (const [key, val] of searchParams.entries()) {
    if (key.endsWith("_label")) continue;
    out[key] = val;
  }
  return out;
}

export function removeEmptyFString(obj) {
  return Object.fromEntries(
    Object.entries(obj).filter(([_, v]) => v != null && v != ""),
  );
}

export function sortByPosition(arr) {
  return arr?.sort((a, b) => parseFloat(a.position) - parseFloat(b.position));
}

export const getProjectProgress = (steps) => {
  if (steps?.length > 0) {
    let completed = 0;
    steps.map((step) => {
      if (step.status == 3) {
        completed += 1;
      }
    });
    return parseInt((completed / steps?.length) * 100);
  } else {
    return 0;
  }
};

export function convertPerToEn(num) {
  const id = {
    "۰": "0",
    "۱": "1",
    "۲": "2",
    "۳": "3",
    "۴": "4",
    "۵": "5",
    "۶": "6",
    "۷": "7",
    "۸": "8",
    "۹": "9",
  };
  return num
    ? num.toString().replace(/[^0-9.]/g, function (w) {
        return id[w] || w;
      })
    : "";
}

export const IsUerOwner = () => {
  const org = store.getState().auth.organization;
  const user = store.getState().auth.user;

  if (org) {
    return org?.owner_id == user?.id;
  } else {
    return false;
  }
};

export const IsUerAdmin = () => {
  const org = store.getState().auth.organization;

  if (org) {
    return org?.pivot?.role == 1 || org?.pivot.role == 2;
  } else {
    return false;
  }
};

export function isDateGreater(d1, d2, days) {
  d1 = new Date(d1);
  return +new Date(d2) > d1.setDate(d1.getDate() + (days || 0));
}

export function hasPermission(module) {
  try {
    const user = store.getState().auth.user;
    const organization = store.getState().auth.organization;

    const is_owner = user?.id == organization?.owner_id;
    if (is_owner) {
      return true;
    }

    const roles = store.getState().auth.roles;
    const permissions = roles.map((role) => role.permissions).flat();
    const permission_names = permissions.map((perm) => perm.name);

    if (permission_names.includes(module)) {
      return true;
    }

    return false;
  } catch (error) {
    return false;
  }
}

function getItem(label, key, icon, children, type) {
  return {
    key,
    icon,
    children,
    label,
    type,
  };
}

/* `modules` is passed in by the sidebar so its memo has a real dependency to
 * rebuild on; anything calling this outside a render can leave it out and get
 * whatever the store holds right now. */
export function getPermissionedMenuItems(projectItems, modules = null) {
  try {
    const roles = store.getState().auth.roles;
    const user = store.getState().auth.user;
    const organization = store.getState().auth.organization;

    const is_owner = user?.id == organization?.owner_id;

    const permissions = roles.map((role) => role.permissions).flat();
    const permission_names = permissions.map((perm) => perm.name);

    /* «همه تسک‌ها» is the organization-wide list the dashboard's attention
     * counters link to, and it is its own entry rather than a mode of
     * «تسک‌های من» — a page titled "my tasks" that shows everyone's tasks reads
     * as a bug, not a feature. Only whoever can already see the org-wide
     * counters gets it. */
    const canSeeAllTasks =
      is_owner ||
      permission_names.includes("manage_org") ||
      permission_names.includes("manage_tasks") ||
      permission_names.includes("manage_projects");

    const canManageOrg = is_owner || permission_names.includes("manage_org");

    let items = [
      getItem("پیشخوان", "dashboard", <DashboardOutlined />, [
        getItem("میزکار", "dashboard", <LayoutOutlined />),
        /* The dashboard's numbers view, on its own route. It is an entry here
         * rather than a tab strip on the page because it has to stay reachable
         * from the other three — a strip drawn by the dashboard itself
         * disappears the moment you step onto «همه تسک‌ها». */
        ...(canManageOrg
          ? [getItem("وضعیت سازمان", "stats", <BarChartOutlined />)]
          : []),
        /* One entry, not two: for someone who manages the organization the
         * personal list is a tab inside «همه تسک‌ها», so a second sidebar item
         * pointing at a subset of the same screen is just a fork in the road
         * with no reason to pick a side. */
        canSeeAllTasks
          ? getItem("همه تسک‌ها", "all-tasks", <UnorderedListOutlined />)
          : getItem("تسک‌های من", "my-tasks", <OrderedListOutlined />),
        getItem("تقویم من", "calendar", <CalendarOutlined />),
      ]),
    ];
    items.push(projectItems);

    if (permission_names.includes("manage_users") || is_owner) {
      items.push(
        getItem("کاربران", "users", <TeamOutlined />, [
          getItem("لیست کاربران", "users", <OrderedListOutlined />),
          getItem("نقش ها", "roles", <UsergroupAddOutlined />),
          getItem("تیم ها", "teams", <BuildOutlined />),
        ]),
      );
    }

    /* Optional modules. The workspace picks these during setup and can change
     * them from فضای کاری › ماژول‌ها; permissions still decide who inside a
     * workspace that has the module actually gets the entry. */
    const activeModules = modules ?? enabledModules();
    const modulesLocked = organization?.hasPlan !== true;
    const moduleItem = (label, key, icon) => ({
      ...getItem(label, key, icon),
      locked: modulesLocked,
      extra: modulesLocked ? <CrownFilled /> : undefined,
    });

    if (
      activeModules.includes("customers") &&
      (permission_names.includes("manage_customers") || is_owner)
    ) {
      items.push(moduleItem("مشتریان", "customers", <IdcardOutlined />));
    }


    if (activeModules.includes("chat")) {
      items.push(moduleItem("پیام‌ها", "chat", <MessageOutlined />));
    }

    if (activeModules.includes("letters")) {
      items.push(moduleItem("نامه‌نگاری", "letters", <MailOutlined />));
    }

    if (activeModules.includes("attendance") && (permission_names.includes("manage_attendance") || is_owner)) {
      items.push(
        moduleItem("حضور و غیاب", "attendance", <ScheduleOutlined />),
      );
    }

    /* یک ورودی، دو تب: تیکت‌های مشتریان و پشتیبانی تسکامون. دو آیتمِ هم‌شکل
       در منو فقط این سؤال را می‌سازد که «کدامش؟». */
    items.push(getItem("تیکت‌ها", "tickets", <CustomerServiceOutlined />));

    if (
      permission_names.includes("manage_org") ||
      permission_names.includes("manage_projects") ||
      permission_names.includes("manage_attendance") ||
      is_owner
    ) {
      items.push(getItem("گزارشات", "reports", <BarChartOutlined />));
    }

    if (activeModules.includes("time_tracking")) {
      items.push(
        moduleItem("فعالیت‌ من", "time-tracking", <ClockCircleOutlined />),
      );
    }

    if (activeModules.includes("attendance")) {
      items.push(moduleItem("تردد من", "my-attendance", <ScheduleOutlined />));
    }

    const subSettings = [];

    if (permission_names.includes("manage_org") || is_owner) {
      subSettings.push(
        getItem("سازمان", "organization", <ApartmentOutlined />),
      );
    }

    if (permission_names.includes("manage_templates") || is_owner) {
      subSettings.push(
        getItem("قالب پروژه‌", "projectTemplates", <ClusterOutlined />),
      );
    }

    if (
      permission_names.includes("manage_org") ||
      permission_names.includes("manage_projects") ||
      is_owner
    ) {
      subSettings.push(getItem("برچسب‌", "tags", <TagOutlined />));
    }

    // Billing follows manage_org: the owner always has it implicitly, and a
    // delegated organization manager can buy or renew on the workspace's behalf.
    if (canManageOrg) {
      subSettings.push(getItem("اشتراک", "subscriptions", <CrownOutlined />));
    }

    if (
      permission_names.includes("manage_templates") ||
      permission_names.includes("manage_org") ||
      permission_names.includes("manage_projects") ||
      is_owner
    ) {
      items.push(
        getItem("تنظیمات", "settings", <SettingOutlined />, subSettings),
      );
    }

    return items;
  } catch (error) {
    return [];
  }
}

export const _scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: "smooth" });
};

export const DEFAULT_ACCENT = "#1677ff";

export const getAccentColor = (organization) => {
  const raw = organization?.color_code;
  if (typeof raw !== "string") return DEFAULT_ACCENT;
  const value = raw.trim();
  return /^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(value)
    ? value
    : DEFAULT_ACCENT;
};

export const getAccentRgb = (organization) => {
  const hex = getAccentColor(organization).substring(1);
  const full =
    hex.length === 3
      ? hex
          .split("")
          .map((c) => c + c)
          .join("")
      : hex.substring(0, 6);
  const rgb = parseInt(full, 16);
  return `${(rgb >> 16) & 0xff}, ${(rgb >> 8) & 0xff}, ${rgb & 0xff}`;
};

/**
 * Black or white, whichever stays readable on the organization's own colour.
 *
 * Exposed as `--ds-accent-fg` (set beside `--ds-accent` in App.js) so anything
 * that paints a solid accent chip — the Gantt's «امروز» marker, for one — can
 * pick its text colour in CSS. A hardcoded white is fine on the default blue
 * and unreadable the moment an organization picks a pale accent.
 */
export const getAccentForeground = (organization) => {
  const hex = getAccentColor(organization).substring(1);
  const full =
    hex.length === 3
      ? hex
          .split("")
          .map((c) => c + c)
          .join("")
      : hex.substring(0, 6);
  const rgb = parseInt(full, 16);
  const brightness =
    (((rgb >> 16) & 0xff) * 299 +
      ((rgb >> 8) & 0xff) * 587 +
      (rgb & 0xff) * 114) /
    1000;

  return brightness > 150 ? "#0f172a" : "#ffffff";
};

export const getContrastColor = (hexColor) => {
  const c = hexColor.substring(1);
  const rgb = parseInt(c, 16);
  const r = (rgb >> 16) & 0xff;
  const g = (rgb >> 8) & 0xff;
  const b = rgb & 0xff;
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 200 ? "dark-text" : "";
};

export function toPersianNumber(num) {
  return String(num).replace(/\d/g, (d) => "۰۱۲۳۴۵۶۷۸۹"[parseInt(d)]);
}

export function formatElapsedTimeFa(start, end) {
  const startDate = dayjs(start);
  const endDate = dayjs(end);

  let diff = endDate.diff(startDate, "second");
  const months = Math.floor(diff / (30 * 24 * 3600));
  diff %= 30 * 24 * 3600;
  const days = Math.floor(diff / (24 * 3600));
  diff %= 24 * 3600;
  const hours = Math.floor(diff / 3600);
  diff %= 3600;
  const minutes = Math.floor(diff / 60);
  const seconds = diff % 60;

  const parts = [];

  if (months) parts.push(`${toPersianNumber(months)} ماه`);
  if (days) parts.push(`${toPersianNumber(days)} روز`);
  if (hours) parts.push(`${toPersianNumber(hours)} ساعت`);
  if (minutes) parts.push(`${toPersianNumber(minutes)} دقیقه`);
  if (seconds) parts.push(`${toPersianNumber(seconds)} ثانیه`);

  return parts.join(" ");
}

moment.loadPersian({ dialect: "persian-modern" });
export function formatDateDescJalaali(dateString) {
  return moment(dateString, "YYYY-MM-DD").format("dddd jD jMMMM jYYYY");
}

let hex2rgb = (c) => `rgb(${c.match(/\w\w/g).map((x) => +`0x${x}`)})`;
export const _getHeaderColor = (c) => {
  try {
    const color = hex2rgb(c);
    var pattern = /rgb\((\d+),\s?(\d+),\s?(\d+)\)/;
    var matches = color.match(pattern);
    var o = Math.round(
      (parseInt(matches[1]) * 299 +
        parseInt(matches[2]) * 587 +
        parseInt(matches[3]) * 114) /
        1000,
    );
    if (o > 170) {
      return "#444444";
    } else {
      return "white";
    }
  } catch (error) {
    return "#000";
  }
};

export const shouldShowOnBoarding = () => {
  const { auth, common } = store.getState();
  const user = auth.user;

  if (
    common?.tourReplay &&
    common.tourReplay === tourFlagForPath(window.location.pathname)
  ) {
    return true;
  }

  const createdDate = new Date(user?.created_at);
  const today = new Date();

  const diffTime = Math.abs(today.getTime() - createdDate.getTime());

  const diffDays = diffTime / (1000 * 60 * 60 * 24);

  return diffDays < 10;
  return true;
};

export const handleDownloadChart = async (id, type) => {
  if (type === "png") {
    const uri = await ApexCharts.getChartByID(id).dataURI({
      scale: 2,
      width: 1200,
      height: 800,
    });
    const link = document.createElement("a");
    link.download = `${id}-${new Date().toISOString()}.${type}`;
    link.href = uri.imgURI;
    link.click();
  }
};

/* Downloads a stored file instead of navigating to it. A bare `<a download>`
 * is ignored cross-origin — the browser navigates and the image just opens in a
 * tab — so the bytes are pulled first and saved from a blob URL. If the fetch is
 * blocked (CORS, expired signature) we fall back to opening the link, which is
 * still better than doing nothing. */
export const downloadFile = async (url, fileName) => {
  if (!url) return;

  try {
    const response = await fetch(url, { credentials: "omit" });
    if (!response.ok) throw new Error(response.statusText);

    const blobUrl = URL.createObjectURL(await response.blob());
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = fileName || url.split("/").pop() || "download";
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(blobUrl);
  } catch (error) {
    window.open(url, "_blank", "noopener");
  }
};

export function PersianGreeting() {
  const now = new Date();
  const hours = now.getHours();

  let greeting = "🌙 شب بخیر";
  if (hours >= 5 && hours < 12) greeting = "☀️ صبح بخیر";
  else if (hours >= 12 && hours < 15) greeting = "😎 ظهر بخیر";
  else if (hours >= 15 && hours < 20) greeting = "🌆 عصر بخیر";

  const persianDate = new Intl.DateTimeFormat("fa-IR", {
    day: "numeric",
    month: "long",
  }).format(now);

  return (
    <span style={{ fontSize: 11 }}>
      {greeting} - {persianDate}
    </span>
  );
}

export function toIranTimeString(date) {
  const pad = (n) => String(n).padStart(2, "0");

  const iranDate = new Date(date.toLocaleString("en-US"));

  const year = iranDate.getFullYear();
  const month = pad(iranDate.getMonth() + 1);
  const day = pad(iranDate.getDate());
  const hours = pad(iranDate.getHours());
  const minutes = pad(iranDate.getMinutes());
  const seconds = pad(iranDate.getSeconds());

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}

export const formatTime = (seconds) => {
  const days = Math.floor(seconds / (3600 * 24));
  const hrs = String(Math.floor((seconds % (3600 * 24)) / 3600)).padStart(
    2,
    "0",
  );
  const mins = String(Math.floor((seconds % 3600) / 60)).padStart(2, "0");
  const secs = String(seconds % 60).padStart(2, "0");
  return `${days > 0 ? `${days} روز و ` : ""}${hrs}:${mins}:${secs}`;
};

/* Persian digits for numbers that sit inside Persian prose. The panel already
 * had two private copies of this — the organization page and the project
 * dashboard — which is why it lives here now for anything written since. */
export const toFa = (value) =>
  String(value ?? "").replace(/\d/g, (digit) => "۰۱۲۳۴۵۶۷۸۹"[+digit]);
